// Wardroom zone — discussions, comments, standups, wiki, whiteboards, bookmarks, polls, retros, reviews, surveys
import type {
  Survey, SurveySubmission, Comment, Whiteboard, WhiteboardElement,
  Discussion, DiscussionReply, Standup, WikiPage, WikiVersion,
  ReviewRequest, Poll, Bookmark, Retrospective, RetroItem,
} from './types'
import type { RequestHelper } from './client'

export function wardroomMethods(r: RequestHelper) {
  return {
    // Surveys
    surveys: {
      list: (projectId: string) => r.get<{ surveys: Survey[] }>(`/hub/projects/${projectId}/surveys`),
      get: (id: string) => r.get<{ survey: Survey }>(`/hub/surveys/${id}`),
      create: (projectId: string, data: Partial<Survey>) => r.post<{ survey: Survey }>(`/hub/projects/${projectId}/surveys`, data),
      update: (id: string, data: Partial<Survey>) => r.put<{ survey: Survey }>(`/hub/surveys/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/surveys/${id}`),
      submit: (id: string, responses: Record<string, unknown>) => r.post<void>(`/hub/surveys/${id}/submit`, { responses }),
      approve: (id: string) => r.post<void>(`/hub/surveys/${id}/approve`),
      requestApproval: (id: string) => r.post<void>(`/hub/surveys/${id}/request-approval`),
      submissions: (id: string) => r.get<{ submissions: SurveySubmission[] }>(`/hub/surveys/${id}/submissions`),
    },

    // Whiteboards
    whiteboards: {
      list: (projectId: string) => r.get<{ whiteboards: Whiteboard[] }>(`/hub/projects/${projectId}/whiteboards`),
      get: (id: string) => r.get<{ whiteboard: Whiteboard; elements: WhiteboardElement[] }>(`/hub/whiteboards/${id}`),
      create: (projectId: string, data: { title: string; description?: string }) => r.post<{ whiteboard: Whiteboard }>(`/hub/projects/${projectId}/whiteboards`, data),
      update: (id: string, data: Partial<Whiteboard>) => r.put<{ whiteboard: Whiteboard }>(`/hub/whiteboards/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/whiteboards/${id}`),
      elements: {
        create: (whiteboardId: string, data: Partial<WhiteboardElement>) => r.post<{ element: WhiteboardElement }>(`/hub/whiteboards/${whiteboardId}/elements`, data),
        update: (whiteboardId: string, elementId: string, data: Partial<WhiteboardElement>) => r.put<{ element: WhiteboardElement }>(`/hub/whiteboards/${whiteboardId}/elements/${elementId}`, data),
        delete: (whiteboardId: string, elementId: string) => r.del<void>(`/hub/whiteboards/${whiteboardId}/elements/${elementId}`),
        batch: (whiteboardId: string, updates: Partial<WhiteboardElement>[]) => r.post<void>(`/hub/whiteboards/${whiteboardId}/elements/batch`, { updates }),
      },
    },

    // Comments
    comments: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ comments: Comment[] }>(`/hub/projects/${projectId}/comments${query}`)
      },
      create: (projectId: string, data: { content: string; context?: string; context_id?: string; parent_id?: string }) => r.post<{ comment: Comment }>(`/hub/projects/${projectId}/comments`, data),
      update: (id: string, data: { content: string }) => r.put<{ comment: Comment }>(`/hub/comments/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/comments/${id}`),
      replies: (id: string) => r.get<{ replies: Comment[] }>(`/hub/comments/${id}/replies`),
    },

    // Discussions
    discussions: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ discussions: Discussion[] }>(`/hub/projects/${projectId}/discussions${query}`)
      },
      get: (id: string) => r.get<{ discussion: Discussion; replies: DiscussionReply[] }>(`/hub/discussions/${id}`),
      create: (projectId: string, data: { title: string; content: string; category?: string }) => r.post<{ discussion: Discussion }>(`/hub/projects/${projectId}/discussions`, data),
      update: (id: string, data: Partial<Discussion>) => r.put<{ discussion: Discussion }>(`/hub/discussions/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/discussions/${id}`),
      pin: (id: string) => r.put<void>(`/hub/discussions/${id}/pin`),
      lock: (id: string) => r.put<void>(`/hub/discussions/${id}/lock`),
      reply: (discussionId: string, data: { content: string }) => r.post<{ reply: DiscussionReply }>(`/hub/discussions/${discussionId}/replies`, data),
      updateReply: (replyId: string, data: { content: string }) => r.put<{ reply: DiscussionReply }>(`/hub/discussions/replies/${replyId}`, data),
      deleteReply: (replyId: string) => r.del<void>(`/hub/discussions/replies/${replyId}`),
    },

    // Standups
    standups: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ standups: Standup[] }>(`/hub/projects/${projectId}/standups${query}`)
      },
      today: (projectId: string) => r.get<{ standups: Standup[] }>(`/hub/projects/${projectId}/standups/today`),
      submit: (projectId: string, data: { yesterday: string; today: string; blockers?: string }) => r.post<{ standup: Standup }>(`/hub/projects/${projectId}/standups`, data),
      delete: (id: string) => r.del<void>(`/hub/standups/${id}`),
      stats: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<Record<string, unknown>>(`/hub/projects/${projectId}/standups/stats${query}`)
      },
    },

    // Wiki
    wiki: {
      list: (projectId: string) => r.get<{ pages: WikiPage[] }>(`/hub/projects/${projectId}/wiki`),
      get: (id: string) => r.get<{ page: WikiPage }>(`/hub/wiki/${id}`),
      create: (projectId: string, data: { title: string; body_markdown: string; parent_id?: string }) => r.post<{ page: WikiPage }>(`/hub/projects/${projectId}/wiki`, data),
      update: (id: string, data: { title?: string; body_markdown?: string }) => r.put<{ page: WikiPage }>(`/hub/wiki/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/wiki/${id}`),
      history: (id: string) => r.get<{ versions: WikiVersion[] }>(`/hub/wiki/${id}/history`),
      search: (projectId: string, query: string) => r.get<{ pages: WikiPage[] }>(`/hub/projects/${projectId}/wiki/search?q=${encodeURIComponent(query)}`),
    },

    // Reviews
    reviews: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ reviews: ReviewRequest[] }>(`/hub/projects/${projectId}/reviews${query}`)
      },
      create: (projectId: string, data: Partial<ReviewRequest>) => r.post<{ review: ReviewRequest }>(`/hub/projects/${projectId}/reviews`, data),
      submit: (id: string, data: { feedback: string; status: string }) => r.put<{ review: ReviewRequest }>(`/hub/reviews/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/reviews/${id}`),
    },

    // Polls
    polls: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ polls: Poll[] }>(`/hub/projects/${projectId}/polls${query}`)
      },
      create: (projectId: string, data: Partial<Poll>) => r.post<{ poll: Poll }>(`/hub/projects/${projectId}/polls`, data),
      vote: (projectId: string, pollId: string, data: { option_ids: string[] }) => r.post<void>(`/hub/projects/${projectId}/polls/${pollId}/vote`, data),
      delete: (projectId: string, pollId: string) => r.del<void>(`/hub/projects/${projectId}/polls/${pollId}`),
    },

    // Bookmarks
    bookmarks: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ bookmarks: Bookmark[] }>(`/hub/projects/${projectId}/bookmarks${query}`)
      },
      create: (projectId: string, data: Partial<Bookmark>) => r.post<{ bookmark: Bookmark }>(`/hub/projects/${projectId}/bookmarks`, data),
      update: (id: string, data: Partial<Bookmark>) => r.put<{ bookmark: Bookmark }>(`/hub/bookmarks/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/bookmarks/${id}`),
    },

    // Retrospectives
    retros: {
      list: (projectId: string) => r.get<{ retrospectives: Retrospective[] }>(`/hub/projects/${projectId}/retros`),
      get: (id: string) => r.get<{ retrospective: Retrospective }>(`/hub/retros/${id}`),
      create: (projectId: string, data: { title: string }) => r.post<{ retrospective: Retrospective }>(`/hub/projects/${projectId}/retros`, data),
      updateStatus: (id: string, data: { status: string }) => r.put<void>(`/hub/retros/${id}/status`, data),
      delete: (id: string) => r.del<void>(`/hub/retros/${id}`),
      addItem: (retroId: string, data: { type: string; content: string }) => r.post<{ item: RetroItem }>(`/hub/retros/${retroId}/items`, data),
      voteItem: (retroId: string, itemId: string) => r.post<void>(`/hub/retros/${retroId}/items/${itemId}/vote`),
      updateItem: (itemId: string, data: Partial<RetroItem>) => r.put<void>(`/hub/retros/items/${itemId}`, data),
      deleteItem: (itemId: string) => r.del<void>(`/hub/retros/items/${itemId}`),
    },
  }
}
